import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {Action, Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as SharedActions from '../../shared/core/shared.actions';
import {SetProfile} from '../../shared/core/shared.actions';
import {AuthenticationService} from './authentication.service';
import * as fromRoot from '../../../state/app.state';
import {LoginModel} from "../models/login.model";
import {CommonService} from "../../shared/services/common.service";
import {ForgotPasswordModel} from "../models/forgot-password.model";
import {SetPasswordModel} from "../models/set-password.model";
import {RegisterModel} from "../models/register.model";
import {GenerateOtpModel} from "../models/generate-otp.model";
import {VerifyOtpModel} from "../models/verify-otp.model";
import {roleUrlMap, step} from "../../shared/constants/common.constants";
import {FetchConsentFormModel} from "../models/fetch-consent-form.model";
import {FetchConsentStatusModel} from "../models/fetch-consent-status.model";
import {VerifyUsernameModel} from "../models/verify-username.model";
import {FetchObQuestionsModel} from "../models/fetch-ob-questions.model";
import {
  SaveAnswersModel
} from "../../page-modules/participants-list/models/save-answers.model";
import {
  ClearPhoneNumber,
  FetchConsentFormLinkFailure,
  FetchConsentFormLinkRequest,
  FetchConsentFormLinkSuccess,
  FetchConsentStatusFailure,
  FetchConsentStatusRequest,
  FetchConsentStatusSuccess,
  FetchObQuestionsFailure,
  FetchObQuestionsRequest,
  FetchObQuestionsSuccess,
  ForgotPasswordFailure,
  ForgotPasswordRequest,
  ForgotPasswordSuccess,
  GenerateOtpFailure,
  GenerateOtpRequest,
  GenerateOtpSuccess,
  LoginFailure,
  LoginRequest,
  LoginSuccess,
  LogoutFailure,
  LogoutRequest,
  LogoutSuccess,
  RegisterFailure,
  RegisterRequest,
  RegisterSuccess,
  SaveAnswersFailure,
  SaveAnswersRequest,
  SaveAnswersSuccess,
  SetPasswordFailure,
  SetPasswordRequest,
  SetPasswordSuccess,
  VerifyOtpFailure,
  VerifyOtpForTfaFailure,
  VerifyOtpForTfaRequest,
  VerifyOtpForTfaSuccess,
  VerifyOtpRequest,
  VerifyOtpSuccess,
  VerifyUsernameFailure,
  VerifyUsernameRequest,
  VerifyUsernameSuccess
} from "./authentication.actions";
import {VerifyOtpForTfaModel} from "../models/verify-otp-for-tfa.model";


@Injectable()
export class AuthenticationEffects {
  login$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(LoginRequest),
      map((action: any) => {
        this.store.dispatch(ClearPhoneNumber());
        this.commonService.removeLoginOTPToken();
        this.commonService.removeRegisterOtpToken();
        return action.payload;
      }),
      mergeMap((payload: LoginModel['request']) =>
        this.authenticationService.login(payload).pipe(
          map((response: LoginModel['response']) => {
            const {data, message} = response;
            const token: string = data.token;
            let profile = null;

            if (token && !data.profile && !data.onGoingStep) {
              this.commonService.setLoginOTPToken(token);
              this.store.dispatch(GenerateOtpSuccess({phoneNumber: data.phoneNumber, message: ''}));
              this.router.navigate(['auth/two-factor-authentication']);
              this.authenticationService.generateOtpSuccess.next(true);
            } else if (token && data.profile) {
              this.commonService.setAuthenticationToken(token);
              profile = {
                ...data.profile,
                roleName: data.profile?.role?.value,
                role: data.profile?.role?.key,
              }
            } else if (token && data.onGoingStep) {
              this.commonService.setRegisterOtpToken(token);
            }
            const loginInfo = {
              onGoingStep: data.onGoingStep,
              profile,
            }
            return LoginSuccess({loginInfo, message});
          }),
          catchError((e) => {
            return of(LoginFailure());
          }),
          tap((action: any) => {
            if (action.type === LoginSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (action.loginInfo.onGoingStep) {
                this.router.navigate([`auth/register/step-${action.loginInfo.onGoingStep}`]);
              } else if (action.loginInfo.profile) {
                const role = action.loginInfo.profile.role;
                this.store.dispatch(SharedActions.SetProfile({profile: action.loginInfo.profile}));
                this.commonService.notification(
                  action.message,
                  'success'
                );
                this.router.navigate([`/${roleUrlMap[role]}`]);
              }

            } else if (action.type === LoginFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  forgotPassword$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ForgotPasswordRequest),
      map((action) => {
        this.authenticationService.forgotPasswordRequest.next();
        return action.payload;
      }),
      mergeMap((payload: ForgotPasswordModel['request']) =>
        this.authenticationService.forgotPassword(payload).pipe(
          map((response: ForgotPasswordModel['response']) => {
            return ForgotPasswordSuccess({message: response.message});
          }),
          catchError(() => {
            return of(ForgotPasswordFailure());
          }),
          tap((action) => {
            if (action.type === ForgotPasswordSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.authenticationService.forgotPasswordSuccess.next();
              this.commonService.notification(
                action.message,
                'success',
              );
            } else if (action.type === ForgotPasswordFailure.type) {
              // Code to execute on API Failure Action dispatch
              this.authenticationService.forgotPasswordFailure.next();
            }
          })
        )
      )
    )
  );

  setPassword$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SetPasswordRequest),
      map((action) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload: SetPasswordModel['request']) =>
        this.authenticationService.setPassword(payload).pipe(
          map((response: SetPasswordModel['response']) => {
            return SetPasswordSuccess({message: response.message});
          }),
          catchError((error) => {
            return of(SetPasswordFailure(error));
          }),
          tap((action) => {
            if (action.type === SetPasswordSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification(action.message, 'success');
              this.router.navigate(
                ['auth/login'],
              ).then(() => {
                this.commonService.removeAuthenticationToken();
                this.commonService.removeSetPasswordToken();
              })
            } else if (action.type === SetPasswordFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  generateOtpRequest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(GenerateOtpRequest),
      map((action) => {
        return action;
      }),
      mergeMap(({payload, resend}) =>
        this.authenticationService.generateOtp(payload).pipe(
          map((response: GenerateOtpModel['response']) => {
            const {message} = response;
            const phoneNumber = payload.phoneNumber;
            return GenerateOtpSuccess({
              phoneNumber,
              message
            });
          }),
          catchError((error) => {
            return of(GenerateOtpFailure({message: error.message}));
          }),
          tap((action) => {
            if (action.type === GenerateOtpSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (!resend) {
                this.router.navigate(['/auth/register/step-3']).then(() => {
                });
              }
              this.commonService.notification(
                action.message,
                "success"
              );
              this.authenticationService.generateOtpSuccess.next(true);
            } else if (action.type === GenerateOtpFailure.type) {
              // Code to execute on API Failure Action dispatch
              this.authenticationService.generateOtpFailure.next();
            }
          })
        )
      )
    )
  );

  verifyOtp$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(VerifyOtpRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.authenticationService.verifyOtp(payload).pipe(
          map((response: VerifyOtpModel['response']) => {
            const {message} = response;
            return VerifyOtpSuccess({
              message
            });
          }),
          catchError((error) => {
            return of(VerifyOtpFailure({message: error.message}));
          }),
          tap((action) => {
            if (action.type === VerifyOtpSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.router.navigate(['auth/register/step-4']).then(() => {
                this.commonService.removeRegisterOtpToken();
                this.commonService.notification(
                  action.message,
                  "success"
                );
              })
            } else if (action.type === VerifyOtpFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  register$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(RegisterRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: RegisterModel['request']) =>
        this.authenticationService.register(payload).pipe(
          map((response: RegisterModel['response']) => {
            const {data, message} = response;
            const token: string = data.token;
            this.commonService.setRegisterOtpToken(token);
            return RegisterSuccess({message});
          }),
          catchError(() => {
            return of(RegisterFailure());
          }),
          tap((action: any) => {
            if (action.type === RegisterSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.router.navigate(['/auth/register/step-2']).then(() => {
                this.commonService.removeRegisterToken();
                this.commonService.notification(action.message, 'success');
              });

            } else if (action.type === RegisterFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    )
  );

  logout$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(LogoutRequest),
      map((action) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap(() =>
        this.authenticationService.logout().pipe(
          map((response: any) => {
            return LogoutSuccess({message: response.message});
          }),
          catchError((error) => {
            return of(LogoutFailure(error.message));
          }),
          tap((action) => {
            if (action.type === LogoutSuccess.type) {
              this.commonService.closeDialog();
              // Code to execute on API Success Action dispatch
              this.commonService.clearAppState();
              this.router.navigate(['/']).then(() => {
                this.commonService.clearAppState();
              });
              this.commonService.notification(
                action.message,
                'success'
              );
            } else if (action.type === LogoutFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  fetchConsentForm$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchConsentFormLinkRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload) =>
        this.authenticationService.fetchConsentFormLink(payload).pipe(
          map((response: FetchConsentFormModel['response']) => {
            const {data, message} = response;
            const consentFormLink = data
            return FetchConsentFormLinkSuccess({consentFormLink, message});
          }),
          catchError((error) => {
            return of(FetchConsentFormLinkFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchConsentFormLinkSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchConsentFormLinkFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  verifyUsername$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(VerifyUsernameRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.authenticationService.verifyUsername(payload).pipe(
          map((response: VerifyUsernameModel['response']) => {
            const {data, message} = response;
            const isAvailable = data.isAvailable
            return VerifyUsernameSuccess({
              message,
              isAvailable
            });
          }),
          catchError((error) => {
            return of(VerifyUsernameFailure({message: error.message}));
          }),
          tap((action) => {
            if (action.type === VerifyUsernameSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === VerifyUsernameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchConsentStatus$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchConsentStatusRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.authenticationService.fetchConsentStatus().pipe(
          map((response: FetchConsentStatusModel['response']) => {
            const {message} = response;
            return FetchConsentStatusSuccess({message});
          }),
          catchError((error) => {
            return of(FetchConsentStatusFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchConsentStatusSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog();
              this.router.navigate(['auth/register/step-5']);
            } else if (action.type === FetchConsentStatusFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchObQuestions$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchObQuestionsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchObQuestionsModel['request']) =>
        this.authenticationService.fetchObQuestions(payload).pipe(
          map((response: FetchObQuestionsModel['response']) => {
            const {data, message} = response;
            const questionList: any = {list: data};
            return FetchObQuestionsSuccess({
              questionList,
              message
            });
          }),
          catchError((error) => {
            return of(FetchObQuestionsFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchObQuestionsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchObQuestionsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  saveAnswers$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SaveAnswersRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: SaveAnswersModel['request']) =>
        this.authenticationService.saveAnswers(payload).pipe(
          map((response: SaveAnswersModel['response']) => {
            const {data, message} = response;
            const saveAnswersInfo = {};
            this.commonService.removeOnboardingToken();
            if (data.token) {
              const token = data.token;
              this.commonService.setAuthenticationToken(token);
            }

            if (data?.profile) {
              saveAnswersInfo['profile'] = {
                ...data.profile,
                roleName: data.profile.role.value,
                role: data.profile.role.key
              }
            }

            return SaveAnswersSuccess({saveAnswersInfo, message});
          }),
          catchError(() => {
            return of(SaveAnswersFailure());
          }),
          tap((action: any) => {
            if (action.type === SaveAnswersSuccess.type) {
              // Code to execute on API Success Action dispatch

              if (action.saveAnswersInfo) {
                this.store.dispatch(SetProfile({profile: action.saveAnswersInfo.profile}));
                this.commonService.removeOnboardingToken();
                if (this.commonService.getAuthenticationToken()) {
                  const role = action.saveAnswersInfo.profile.role;
                  this.router.navigate([`/${roleUrlMap[role]}`]);
                } else {
                  this.router.navigate(['/']);
                }
              }

            } else if (action.type === SaveAnswersFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    )
  );

  verifyOtpForTfa$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(VerifyOtpForTfaRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.authenticationService.verifyOtp(payload).pipe(
          map((response: VerifyOtpForTfaModel['response']) => {
            const {data, message} = response;
            const token: string = data.token;
            const tfaToken: string = data.tfaToken;
            this.commonService.setAuthenticationToken(token);
            if (tfaToken) {
              this.commonService.set2FAT(tfaToken);
            }

            const verifyOtpInfo = {
              onGoingStep: data.onGoingStep,
              ...(data.profile ? {
                profile: {
                  ...data.profile,
                  roleName: data.profile?.role?.value,
                  role: data.profile?.role?.key,
                }
              } : {})
            }
            return VerifyOtpForTfaSuccess({
              verifyOtpInfo,
              message
            });
          }),
          catchError((error) => {
            return of(VerifyOtpForTfaFailure({message: error.message}));
          }),
          tap((action) => {
            if (action.type === VerifyOtpForTfaSuccess.type) {
              if (action.verifyOtpInfo.onGoingStep) {
                if (action.verifyOtpInfo.onGoingStep === step['step-warning']) {
                  this.router.navigate([`auth/step-warning`]);
                } else {
                  this.router.navigate([`auth/register/step-${action.verifyOtpInfo.onGoingStep}`]);
                }
              } else if (action.verifyOtpInfo.profile) {
                const role = action.verifyOtpInfo.profile.role;
                this.store.dispatch(SharedActions.SetProfile({profile: action.verifyOtpInfo.profile}));
                this.commonService.notification(
                  action.message,
                  'success'
                );
                this.router.navigate([`/${roleUrlMap[role]}`]);
              }
            } else if (action.type === VerifyOtpForTfaFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  profile: any;

  constructor(
    private store: Store<fromRoot.State>,
    private authenticationService: AuthenticationService,
    private actions: Actions,
    private router: Router,
    private commonService: CommonService,
  ) {
  }
}
